import { ServiceLayout } from "@/components/ServiceLayout"
import { aiEngineeringFAQs } from "@/data/faqData"

const AIEngineering = () => {
  return (
    <ServiceLayout
      title="AI-Augmented Engineering"
      description="Deploying engineering pods equipped with AI coding assistants and automated QA agents to deliver feature velocity 3x faster than traditional teams."
      heroImage="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=2600"
      faqs={aiEngineeringFAQs}
      faqTitle="AI Engineering & MLOps FAQs"
      faqSubtitle="Common questions about AI development, MLOps, and model deployment"
      features={[
        {
          title: "AI Pair Programming",
          description:
            "Teams equipped with Copilot/Codeium to accelerate boilerplate generation and standard logic implementation.",
        },
        {
          title: "Automated QA Agents",
          description:
            "Bots that autonomously generate and run test cases for every pull request, ensuring regression-free deployments.",
        },
        {
          title: "Technical Debt Prediction",
          description:
            "Analysis tools that flag code complexity and potential refactoring needs before they become critical issues.",
        },
      ]}
      approach={{
        title: "The Bionic Engineering Pod",
        description:
          "We are entering the era of the 10x Engineer, powered by silicon.",
        image:
          "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&q=80&w=2600",
        content: [
          "We don't try to replace engineers; we give them bionic arms. Our 'Bionic Engineering Pod' model augments a senior human architect with a suite of specialized AI agents.",
          "We integrate Copilot/Codeium directly into the IDE to handle boilerplate generation, while 'Repo-Agents' (like Sweep.dev) automatically handle technical debt and dependency updates in the background. This allows your team to focus purely on high-leverage architectural decisions.",
          "Quality is enforced by 'Adversarial QA Swarms' - autonomous testing agents that explore the UI visually to find edge cases that traditional Selenium scripts miss.",
        ],
      }}
      benefits={[
        "3x Faster Time-to-Market",
        "50% Reduction in Bugs",
        "Automated Documentation",
        "Higher Code Quality Standards",
        "Streamlined Code Reviews",
        "Continuous Security Scanning",
      ]}
      process={{
        steps: [
          {
            title: "Toolchain Integration",
            description:
              "Setting up IDE extensions, CI/CD pipelines, and access tokens for AI assistants within your secure environment.",
          },
          {
            title: "Developer Training",
            description:
              "Workshops to teach your team effective prompt engineering strategies for code generation and debugging.",
          },
          {
            title: "Automated QA Setup",
            description:
              "Configuring agents to automatically generate unit tests, integration tests, and visual regression tests for every commit.",
          },
          {
            title: "Velocity Tracking",
            description:
              "Measuring key DORA metrics (Deployment Frequency, Lead Time for Changes) to quantify the impact of AI augmentation.",
          },
        ],
      }}
      techStack={[
        {
          category: "Coding Assistants",
          items: ["GitHub Copilot", "Tabnine", "Codeium", "Cursor"],
        },
        {
          category: "CI/CD & DevOps",
          items: ["GitHub Actions", "GitLab CI", "ArgoCD", "Jenkins"],
        },
        {
          category: "Quality Assurance",
          items: ["SonarQube", "Jest", "Cypress", "Selenium"],
        },
      ]}
    />
  )
}

export default AIEngineering
